import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchSettings,
  selectSettingsGroup,
  updateSettingItem,
} from '@main/features/settings/settingsSlice';
import { UiButton, UiCheckbox, UiInput } from '@shared/common/ui';

const FINANCE_GROUP = 'finance';
const PSN_LIMITS_KEY = 'psn_limits';
const PAYKEEPER_KEY = 'paykeeper';

const moneyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

/**
 * Лимиты ПСН для виджета «Оборот». Хранятся в настройках владельца, чтобы
 * пороги можно было менять без релиза (ТЗ «Виджет Оборот», §11).
 */
export function FinanceSettings() {
  const dispatch = useDispatch();
  const finance = useSelector(selectSettingsGroup(FINANCE_GROUP));
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paykeeperDraft, setPaykeeperDraft] = useState(null);
  const [paykeeperSaving, setPaykeeperSaving] = useState(false);

  useEffect(() => {
    if (finance.status === 'idle') dispatch(fetchSettings(FINANCE_GROUP));
  }, [dispatch, finance.status]);

  const saved = useMemo(() => {
    const item = finance.items.find((it) => it.key === PSN_LIMITS_KEY);
    const limits = Array.isArray(item?.value?.limits) && item.value.limits.length
      ? item.value.limits
      : [15000000, 20000000];
    return {
      enabled: item?.value?.enabled !== false,
      limits: limits.map((it) => String(it)),
    };
  }, [finance.items]);

  const form = draft ?? saved;
  const paykeeperDisabled = paykeeperDraft ??
    (finance.items.find((it) => it.key === PAYKEEPER_KEY)?.value?.disabled === true);

  const setLimit = (index, value) =>
    setDraft({
      ...form,
      limits: form.limits.map((it, i) => (i === index ? value.replace(/[^\d]/g, '') : it)),
    });

  const removeLimit = (index) =>
    setDraft({ ...form, limits: form.limits.filter((_, i) => i !== index) });

  const addLimit = () => setDraft({ ...form, limits: [...form.limits, ''] });

  const onSave = async () => {
    const limits = form.limits
      .map((it) => Number.parseInt(it, 10))
      .filter((it) => Number.isFinite(it) && it > 0);
    setSaving(true);
    const result = await dispatch(
      updateSettingItem({
        group: FINANCE_GROUP,
        key: PSN_LIMITS_KEY,
        value: { enabled: form.enabled, limits },
      }),
    );
    setSaving(false);
    if (result.meta.requestStatus === 'fulfilled') setDraft(null);
  };

  const onSavePaykeeper = async () => {
    setPaykeeperSaving(true);
    const result = await dispatch(
      updateSettingItem({
        group: FINANCE_GROUP,
        key: PAYKEEPER_KEY,
        value: { disabled: paykeeperDisabled },
      }),
    );
    setPaykeeperSaving(false);
    if (result.meta.requestStatus === 'fulfilled') setPaykeeperDraft(null);
  };

  if (finance.status === 'loading') return <div className="placeholder">Загрузка…</div>;

  return (
    <div style={{ display: 'grid', gap: 24, maxWidth: 620 }}>
      {finance.error && <div className="banner banner--danger">{finance.error}</div>}
      {finance.saveError && <div className="banner banner--danger">{finance.saveError}</div>}
      <div className="form-card">
        <h2>Лимиты ПСН</h2>
        <p className="muted">
          Пороги выручки для блока контроля ПСН в виджете «Оборот» на странице профиля.
          Виджет сравнивает с ними оборот за текущий год.
        </p>

        <UiCheckbox
          label="Показывать контроль лимитов ПСН"
          checked={form.enabled}
          onChange={(e) => setDraft({ ...form, enabled: e.target.checked })}
        />

        <div style={{ display: 'grid', gap: 8 }}>
          {form.limits.map((limit, index) => (
            // Строка лимита — просто число, стабильного ключа у неё нет.
            <div key={index} style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
              <UiInput
                label={`Лимит ${index + 1}, ₽`}
                inputMode="numeric"
                value={limit}
                onChange={(e) => setLimit(index, e.target.value)}
              />
              <span className="muted" style={{ paddingBottom: 10, whiteSpace: 'nowrap' }}>
                {limit ? moneyFormatter.format(Number(limit)) : '—'}
              </span>
              <UiButton
                type="button"
                variant="ghost"
                size="sm"
                style={{ marginBottom: 2 }}
                onClick={() => removeLimit(index)}
              >
                Удалить
              </UiButton>
            </div>
          ))}
          {form.limits.length === 0 && (
            <div className="placeholder">
              Лимитов нет — блок ПСН в виджете покажет только оборот.
            </div>
          )}
        </div>

        <div className="form-actions">
          <UiButton type="button" variant="ghost" disabled={form.limits.length >= 5} onClick={addLimit}>
            Добавить лимит
          </UiButton>
          <UiButton type="button" disabled={saving || !draft} onClick={onSave}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </UiButton>
        </div>
      </div>
      <div className="form-card">
        <h2>PayKeeper</h2>
        <p className="muted">
          Наличные продажи всех клиентов сохраняются без отправки чека в PayKeeper.
          QR-оплата и оплата через сайт продолжают работать. После включения снова действуют
          индивидуальные настройки клиентов.
        </p>
        <UiCheckbox
          label="Отключить PayKeeper"
          checked={paykeeperDisabled}
          onChange={(e) => setPaykeeperDraft(e.target.checked)}
        />
        <div className="form-actions">
          <UiButton type="button" disabled={paykeeperSaving || paykeeperDraft === null} onClick={onSavePaykeeper}>
            {paykeeperSaving ? 'Сохраняем…' : 'Сохранить'}
          </UiButton>
        </div>
      </div>
    </div>
  );
}
